import React from 'react';
import { useState } from 'react';
// nodejs library to set properties for components
import PropTypes from 'prop-types';
// nodejs library that concatenates classes
import classNames from 'classnames';

import { DatePicker } from '@mui/lab';
import { FormControl } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { useTranslation } from 'react-i18next';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import frLocale from 'date-fns/locale/fr';
import enLocale from 'date-fns/locale/en-CA';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';

const useStyles = makeStyles(theme => ({}));

export default function CustomDatePicker({
  formControlProps,
  value,
  labelText,
  id,
  datePickerClasses,
  datePickerProps,
  error,
  success,
  helperText,
  hasSkeleton,
  skeletonAnimation,
  skeletonVariant,
  skeletonProps,
  isLoading,
  dateFormat,
  ampm,
  disablePast,
  disableFuture,
  isYear,
  isMonths,
  disabled,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [dateOpen, setDateOpen] = useState(false);

  const dateFormatValue = dateFormat || t('date_format');

  var formControlClasses;
  if (formControlProps !== undefined) {
    formControlClasses = classNames(formControlProps.className, classes.formControl);
  } else {
    formControlClasses = classes.formControl;
  }
  let infoToDisplay = ['year', 'month', 'day'];
  let inputFormat = t('date_format_datepicker');

  if (isYear) {
    infoToDisplay = ['year'];
    inputFormat = 'yyyy';
  }
  if (isMonths) {
    infoToDisplay = ['month'];
    inputFormat = 'MMMM';
  }
  let selectedLocale = t('locale') === 'fr' ? frLocale : enLocale;

  if (hasSkeleton && isLoading) {
    return (
      <SkeletonWrapper
        animation={skeletonAnimation}
        variant={skeletonVariant}
        skeletonProps={skeletonProps}
        fullWidth={formControlProps?.fullWidth}
        isLoading
        component={
          <FormControl {...formControlProps} className={formControlClasses}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={selectedLocale}>
              <DatePicker
                locale="fr"
                disabled={disabled}
                className={datePickerClasses}
                dateFormat={dateFormatValue}
                mask={t('date_format_mask')}
                inputFormat={inputFormat}
                views={infoToDisplay}
                renderInput={({ inputRef, inputProps, InputProps }) => (
                  <CustomInput
                    labelText={labelText}
                    id={id}
                    inputProps={{
                      ...inputProps,
                      onClick: _ => setDateOpen(true),
                      endAdornment: InputProps?.endAdornment,
                      inputRef: inputRef,
                    }}
                  />
                )}
                onChange={_ => {}}
              />
            </LocalizationProvider>
          </FormControl>
        }
      />
    );
  }
  return (
    <FormControl {...formControlProps} className={formControlClasses}>
      <LocalizationProvider dateAdapter={AdapterDateFns} locale={selectedLocale}>
        <DatePicker
          className={datePickerClasses}
          open={dateOpen}
          onOpen={() => setDateOpen(true)}
          onClose={() => setDateOpen(false)}
          views={infoToDisplay}
          ampm={ampm}
          label={labelText}
          value={value}
          disablePast={disablePast}
          disableFuture={disableFuture}
          mask={t('date_format_mask')}
          renderInput={({ inputRef, inputProps, InputProps }) => (
            <CustomInput
              labelText={labelText}
              id={id}
              error={error}
              inputProps={{
                ...inputProps,
                onClick: _ => setDateOpen(true),
                endAdornment: InputProps?.endAdornment,
                inputRef: inputRef,
              }}
            />
          )}
          dateFormat={'dateFormatValue'}
          inputFormat={inputFormat}
          onChange={_ => {}}
          {...datePickerProps}
        />
      </LocalizationProvider>
    </FormControl>
  );
}

CustomDatePicker.propTypes = {
  formControlProps: PropTypes.object,
  labelText: PropTypes.string,
  id: PropTypes.string,
  datePickerProps: PropTypes.object,
  datePickerClasses: PropTypes.string,
  error: PropTypes.string,
  success: PropTypes.bool,
  helperText: PropTypes.string,
  hasSkeleton: PropTypes.bool,
  skeletonAnimation: PropTypes.string,
  skeletonVariant: PropTypes.string,
  skeletonProps: PropTypes.object,
  isLoading: PropTypes.bool,
};
